import React from 'react';
import Tree from 'rc-tree';
import '../css/tree-view.css';
import '../css/virtualized-tree.css';
import 'rc-tree/assets/index.css';
import {connect} from 'react-redux';
import {putDataInDevCenterDataStore} from '../../../../../app/store';
import {removeItemsToJsonTree,compareTwoArraysAndRemoveDuplicates,iterateJsonAndGetPrimitiveJson} from '../js/js-tree-operations';
import {sortCheckedList} from '../../../actions/rhs-schema-actions';
import {getJsTreeChecked,getExampleValue} from '../../../../dynamic/reducers/examples-reducer';
import {getJsTree,getJsTreeExpanded,getJsTreeExpandedFlag,getSchema} from '../../../../dynamic/reducers/rhs-schema-reducer';
import showDivBasedOnId, {  clickDivBasedOnId, hideDivBasedOnId, setDisplayFlexBasedOnId, setDisplayVisibleBasedOnId } from '../../../../../commons/ui-commons';
import CybsIcon from '@cybs-ui/components/lib/icon';

class VirtualizedJsTree extends React.Component {
    state = {
        checked: [],
        clicked: {},
        expanded: []
    };

    constructor(props) {
        super(props);
        
        this.onCheck = this.onCheck.bind(this);
        this.onExpand = this.onExpand.bind(this);
        this.onClick = this.onClick.bind(this);
        
        // Performance monitoring
        this.performanceStart = null;
        this.renderCount = 0;
    }

    componentDidMount() {
        // Set up accessibility attributes for info icons after mount
        setTimeout(() => {
            this.setupInfoIconAccessibility();
        }, 100);
    }

    componentDidUpdate(prevProps) {
        // Update accessibility attributes after each update
        setTimeout(() => {
            this.setupInfoIconAccessibility();
        }, 10);
        
        // Refresh CodeMirror when example value changes from external sources (like dropdown)
        if (prevProps.exampleValue !== this.props.exampleValue) {
            setTimeout(() => {
                const editor = document.querySelector('.CodeMirror');
                if (editor && editor.CodeMirror) {
                    editor.CodeMirror.refresh();
                }
            }, 50);
        }
    }

    setupInfoIconAccessibility() {
        // Set up aria-label for info icons like the original tree
        let titleInfoElements = document.querySelectorAll('.virtualized-js-tree-container .info--tiny');
        for (var i = 0; i < titleInfoElements.length; i++) {
            let titleElement = titleInfoElements[i].parentElement;
            if (titleElement) {
                titleInfoElements[i].setAttribute('aria-label', "Opens dialog for field " + titleElement.textContent.trim());
            }
        }
    }

    onCheck(checkedKeys, info) {
        
        try {
            // Get the target node info for compatibility with existing function
            const targetNodeValue = info.node?.key || (info.checkedNodes?.[0]?.key);
            const targetNode = { value: targetNodeValue };
            
            // Call the existing function that handles all the live console integration
            this.addItemsToCheckedList(checkedKeys, targetNode);
        } catch (error) {
            console.error('Error in onCheck handler:', error);
        }
    }

    addItemsToCheckedList(checkedItems, targetNode) {
        
        var prevCheckItems = getJsTreeChecked();
        var jsTreeContents = getJsTree();
        var sortedCheckedItems = sortCheckedList(checkedItems, jsTreeContents);
        
        putDataInDevCenterDataStore({type:'UPDATE_JS_TREE_CHECK_BOX',jsTreeChecked:sortedCheckedItems});
        
        var exampleValue = JSON.parse(getExampleValue());
        
        if(prevCheckItems != undefined) {
            var newExampleValue = {};
            if(prevCheckItems.length < sortedCheckedItems.length) {
                // Items were added - Build in schema order to maintain proper JSON structure
                newExampleValue = this.buildJsonInSchemaOrder(sortedCheckedItems, exampleValue, jsTreeContents);
            } else {
                // Items were removed - Remove from exampleValue directly
                prevCheckItems.sort().reverse();
                checkedItems.sort().reverse();
                var uncheckedContents = compareTwoArraysAndRemoveDuplicates(prevCheckItems,checkedItems);
                for(var items in uncheckedContents) {
                    exampleValue = removeItemsToJsonTree(uncheckedContents[items],exampleValue,null,jsTreeContents);
                }
            }

            if(JSON.stringify(newExampleValue) === '{}'){
                newExampleValue = exampleValue;
            }  

            // Update live console
            if("properties" in getSchema()) {
                var schemaValue = getSchema();
                putDataInDevCenterDataStore({type:'UPDATE_REQUEST_VALUE',requestValue:iterateJsonAndGetPrimitiveJson(schemaValue["properties"],newExampleValue)});
            }
        }
        
        // Final update to example value
        putDataInDevCenterDataStore({type:'UPDATE_EXAMPLE_VALUE',exampleValue:JSON.stringify(newExampleValue, null, 2)});
        
        // Force CodeMirror refresh after tree selection changes
        setTimeout(() => {
            const editor = document.querySelector('.CodeMirror');
            if (editor && editor.CodeMirror) {
                editor.CodeMirror.refresh();
            }
        }, 50);
        
    }

    // Build JSON object in schema order to maintain proper structure
    buildJsonInSchemaOrder(checkedItems, exampleValue, jsTreeContents) {
        
        // Create a Set for fast lookup of checked items
        const checkedSet = new Set(checkedItems);
        
        // Build the object by traversing the schema tree in order
        const newExampleValue = {};
        this.traverseSchemaAndBuild(jsTreeContents, '', checkedSet, exampleValue, newExampleValue);

        return newExampleValue;
    }

    // Recursively traverse schema tree and build JSON in correct order
    traverseSchemaAndBuild(treeNodes, parentPath, checkedSet, exampleValue, targetObject) {
        if (!treeNodes || !Array.isArray(treeNodes)) return;
        
        for (const node of treeNodes) {
            const currentPath = parentPath ? `${parentPath}-${node.value.split('-').pop()}` : node.value;
            
            // Check if this field or any of its children are checked
            const isFieldChecked = checkedSet.has(currentPath);
            const hasCheckedChildren = this.hasCheckedDescendants(node, currentPath, checkedSet);
            
            if (isFieldChecked || hasCheckedChildren) {
                // Extract the field name (last part of the path)
                const fieldName = node.value.split('-').pop().replace(/\*/g, '_');
                
                if (node.children && node.children.length > 0) {
                    // This is a parent object/array
                    if (node.type === 'array') {
                        targetObject[fieldName] = hasCheckedChildren ? [{}] : [];
                        if (hasCheckedChildren) {
                            this.traverseSchemaAndBuild(node.children, currentPath, checkedSet, 
                                exampleValue[fieldName] ? exampleValue[fieldName][0] : {}, 
                                targetObject[fieldName][0]);
                        }
                    } else {
                        // Object type
                        targetObject[fieldName] = {};
                        this.traverseSchemaAndBuild(node.children, currentPath, checkedSet, 
                            exampleValue[fieldName] || {}, 
                            targetObject[fieldName]);
                    }
                } else if (isFieldChecked) {
                    // This is a leaf field that's checked
                    targetObject[fieldName] = exampleValue[fieldName] || "";
                }
            }
        }
    }

    // Check if a node has any checked descendants
    hasCheckedDescendants(node, currentPath, checkedSet) {
        if (!node.children) return false;
        
        for (const child of node.children) {
            const childPath = `${currentPath}-${child.value.split('-').pop()}`;
            if (checkedSet.has(childPath)) {
                return true;
            }
            if (this.hasCheckedDescendants(child, childPath, checkedSet)) {
                return true;
            }
        }
        return false;
    }

    onExpand(expandedKeys, info) {
        
        this.setState({ expanded: expandedKeys });

        hideDivBasedOnId("JstreeLoaderIndicator");
        showDivBasedOnId("requestBuilderJsTreeTab");
        hideDivBasedOnId("BodyTabLoaderIndicator");
        showDivBasedOnId("bodyTab");
        setDisplayFlexBasedOnId("sample-request-data");
        clickDivBasedOnId("bodyTab");
        setDisplayFlexBasedOnId("live-console");
        setDisplayVisibleBasedOnId("live-console");
        clickDivBasedOnId("live-console");
        
        var editor = document.querySelector('.CodeMirror');
        if (editor && editor.CodeMirror) {
            setTimeout(function() {
                editor.CodeMirror.refresh();
                let titleInfoElements = document.querySelectorAll('.virtualized-js-tree-container .info--tiny');
                for (var i = 0; i < titleInfoElements.length; i++) {
                    let titleElement = titleInfoElements[i].parentElement;
                    if (titleElement) {
                        titleInfoElements[i].setAttribute('aria-label', "Opens dialog for field " + titleElement.textContent.trim());
                    }
                }
            }, 1);
        }
    }

    onClick(info) {
        if (info.node && info.node.originalNode) {
            const originalNode = info.node.originalNode;
            if (typeof originalNode.label === 'object') {
                putDataInDevCenterDataStore({type: 'UPDATE_NODE_CLICK_CHECK', nodeClickCheck: true});
                putDataInDevCenterDataStore({type: 'UPDATE_CLICKED_NODE', clickedNode: 'request-Body-' + originalNode.value});
            }
        }
    }

    // Enhanced transform function with proper styling and info icons
    transformNodes(nodes) {
        
        if (!nodes || !Array.isArray(nodes)) {
            return [];
        }

        const transformNode = (node, depth = 0) => {

            // Handle React elements in labels (like icons) and preserve info icons
            let nodeTitle = node.value; // Default fallback
            let hasInfoIcon = false;
            
            if (typeof node.label === 'string') {
                nodeTitle = node.label;
            } else if (React.isValidElement(node.label)) {
                // Check if this is a label with info icon (React element with children)
                if (node.label.props && node.label.props.children) {
                    if (Array.isArray(node.label.props.children)) {
                        // Extract text and check for info icon
                        const textContent = node.label.props.children[0];
                        const iconElement = node.label.props.children[1];
                        
                        if (typeof textContent === 'string') {
                            nodeTitle = textContent.trim();
                        }
                        
                        // Check if there's an info icon
                        if (React.isValidElement(iconElement) && 
                            iconElement.props && 
                            iconElement.props.name === 'info') {
                            hasInfoIcon = true;
                        }
                    } else {
                        nodeTitle = this.extractTextFromReactElement(node.label) || node.value;
                    }
                } else {
                    nodeTitle = this.extractTextFromReactElement(node.label) || node.value;
                }
            } else if (node.label) {
                nodeTitle = String(node.label);
            }

            const transformed = {
                key: node.value,
                title: nodeTitle,
                children: node.children ? node.children.map((child) => transformNode(child, depth + 1)) : undefined,
                
                // Enhanced property mapping
                checkable: node.showCheckbox !== false,
                disabled: node.disabled || false,
                
                // Preserve important styling classes
                className: this.buildNodeClassName(node),
                
                // Store original node data for reference in event handlers
                originalNode: {
                    value: node.value,
                    label: node.label,
                    description: node.description,
                    additionalInformation: node.additionalInformation,
                    type: node.type,
                    required: node.required,
                    className: node.className,
                    hasInfoIcon: hasInfoIcon
                },
                
                // Add level for CSS styling
                level: depth
            };
            
            // Remove undefined children to clean up the structure
            if (!transformed.children || transformed.children.length === 0) {
                delete transformed.children;
            }
            
            return transformed;
        };

        const transformedNodes = nodes.map((node) => transformNode(node));
        
        return transformedNodes;
    }

    // Helper function to extract text from React elements
    extractTextFromReactElement(element) {
        if (typeof element === 'string') {
            return element;
        }
        
        if (React.isValidElement(element)) {
            // Handle React fragments and elements with children
            if (element.props && element.props.children) {
                if (Array.isArray(element.props.children)) {
                    return element.props.children
                        .map(child => this.extractTextFromReactElement(child))
                        .filter(text => text)
                        .join(' ');
                } else {
                    return this.extractTextFromReactElement(element.props.children);
                }
            }
        }
        
        return null;
    }

    // Helper function to build proper CSS class names
    buildNodeClassName(node) {
        const classes = [];
        
        // Preserve original className
        if (node.className) {
            classes.push(node.className);
        }
        
        // Add required field styling
        if (node.required === "true" || node.required === true) {
            classes.push('required-field');
        }
        
        // Add custom required styling
        if (node.className === 'custom-required') {
            classes.push('custom-required-field');
        }
        
        return classes.join(' ');
    }

    render() {

        var jsTreeReducer = getJsTreeExpanded();
        var jsTreeExpandedFlag = getJsTreeExpandedFlag();
        var jsTreeNew = getJsTree();
        
        if((this.state.expanded.length == 0 || jsTreeExpandedFlag) && jsTreeReducer && jsTreeReducer.length){
            putDataInDevCenterDataStore({type:'UPDATE_JS_TREE_EXPANDED_FLAG', jsTreeExpandedFlag: false});
            this.onExpand(jsTreeReducer);
        }
        
        if(!jsTreeNew){
            jsTreeNew = []
        }

        const transformedNodes = this.transformNodes(jsTreeNew);
        
        const checkedKeys = getJsTreeChecked() || [];
        
        const treeComponent = (
            <div className="virtualized-js-tree-container" id="request-tabs">
                <Tree
                    // Core functionality
                    checkable={true}
                    selectable={false}
                    multiple={false}
                    
                    // Data
                    treeData={transformedNodes}
                    checkedKeys={checkedKeys}
                    expandedKeys={this.state.expanded}
                    
                    // Event handlers
                    onCheck={this.onCheck}
                    onExpand={this.onExpand}
                    onClick={this.onClick}
                    
                    // Enhanced props for better compatibility
                    checkStrictly={false}
                    defaultExpandAll={false}
                    showLine={false}
                    showIcon={false}
                    
                    virtual={true}
                    height={530}
                    
                    // Virtual rendering optimization settings
                    itemHeight={30} // Match original tree height
                    
                    // Buffer settings for smooth scrolling
                    // These control how many extra nodes to render outside viewport
                    
                    // Accessibility
                    titleRender={this.renderNodeTitle}
                    
                    // Custom switcher icon for chevron expand/collapse
                    switcherIcon={this.renderSwitcherIcon}
                />
            </div>
        );
        
        return treeComponent;
    }

    // Helper function to count total nodes (including nested)
    countTotalNodes(nodes) {
        if (!nodes || !Array.isArray(nodes)) {
            return 0;
        }
        
        let count = 0;
        const countRecursive = (nodeArray) => {
            for (const node of nodeArray) {
                count++;
                if (node.children && Array.isArray(node.children)) {
                    countRecursive(node.children);
                }
            }
        };
        
        countRecursive(nodes);
        return count;
    }

    // Custom switcher icon renderer for chevron expand/collapse
    renderSwitcherIcon = ({ isLeaf, expanded }) => {
        if (isLeaf) {
            return null; // No icon for leaf nodes
        }
        
        // Return Font Awesome chevron icons like original tree
        return (
            <i 
                className={`fa ${expanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                style={{ 
                    fontSize: '12px', 
                    opacity: 0.5,
                    transition: 'opacity 0.2s'
                }}
            />
        );
    }

    // Enhanced node title rendering with info icons and styling
    renderNodeTitle = (nodeData) => {
        const { title, originalNode, level } = nodeData;
        
        // Check if this node should have an info icon
        const hasInfoIcon = originalNode?.hasInfoIcon;
        
        // Build CSS classes for styling - only use the original className from schema actions
        const titleClasses = ['rc-tree-title-content'];
        if (originalNode?.className) {
            titleClasses.push(originalNode.className); // This will be 'required' or 'custom-required'
        }
        
        const handleInfoClick = (e) => {
            e.stopPropagation(); // Prevent tree node selection
            if (originalNode?.value) {
                // Trigger the same logic as original tree for info icon clicks
                putDataInDevCenterDataStore({type: 'UPDATE_NODE_CLICK_CHECK', nodeClickCheck: true});
                putDataInDevCenterDataStore({type: 'UPDATE_CLICKED_NODE', clickedNode: 'request-Body-' + originalNode.value});
            }
        };
        
        return (
            <span 
                className={titleClasses.join(' ')}
                title={title}
                data-level={level}
            >
                {title}
                {hasInfoIcon && <CybsIcon name='info' resolution='tiny' dataTest='info' onClick={handleInfoClick}/>}
            </span>
        );
    }
}

function mapStateToProps(state){
    return{
        jsTree: state.rhsReducer.jsTree,
        jsTreeChecked:state.examplesReducer.jsTreeChecked,
        nodeClickCheck: state.rhsReducer.nodeClickCheck,
        exampleValue: state.examplesReducer.exampleValue,
    }
}

export default connect(mapStateToProps)(VirtualizedJsTree);
